import { useCallback, useMemo, useRef } from 'react';

import { useOAuthHandlerFactory } from '@atlassian/conversation-assistant-chat-prompt-input/useOAuthHandlerFactory';
import { useNewlyEnabledConnectors } from '@atlassian/conversation-assistant-store/controllers/connectors-store';

const TEMPLATE_ID = 'rovo-chat-input-banner-3p-connector-nudge';

type ConnectorInfo = {
	connectorId: string;
	appName: string;
	logoUrl: string;
	onConnect: () => void;
};

type ConnectorNudgeMessageProps = {
	apps: ConnectorInfo[];
};

type PlacementMessage = Record<string, unknown>;

type ConnectorNudgeMessage = {
	templateId: typeof TEMPLATE_ID;
	isEligible: (message: PlacementMessage) => boolean;
	connectorNudgeMessageProps: ConnectorNudgeMessageProps;
};

export const use3pConnectorNudgeMessage = (): ConnectorNudgeMessage => {
	const newlyEnabledConnectors = useNewlyEnabledConnectors();
	const createOAuthHandler = useOAuthHandlerFactory();

	// Keep a ref to createOAuthHandler so onConnect callbacks can always call
	// the latest version without being listed as a dependency of apps.
	// createOAuthHandler is recreated on every render due to instability in
	// useFlags/useAnalytics, but its behaviour is functionally equivalent each
	// time — the ref pattern avoids propagating that churn downstream.
	const createOAuthHandlerRef = useRef(createOAuthHandler);
	createOAuthHandlerRef.current = createOAuthHandler;

	// apps is memoized only on newlyEnabledConnectors — the connector set.
	// onConnect reads createOAuthHandlerRef.current at call time so it always
	// uses the latest handler without making apps depend on createOAuthHandler.
	const apps = useMemo<ConnectorInfo[]>(
		() =>
			newlyEnabledConnectors
				.filter(
					(
						connector,
					): connector is typeof connector & {
						outboundAuthUrl: string;
						iconUrl: string;
						friendlyName: string;
					} =>
						Boolean(connector.outboundAuthUrl) &&
						Boolean(connector.iconUrl) &&
						Boolean(connector.friendlyName),
				)
				.map((connector) => ({
					connectorId: connector.source,
					appName: connector.friendlyName,
					logoUrl: connector.iconUrl,
					onConnect: () =>
						createOAuthHandlerRef
							.current({
								friendlyName: connector.friendlyName,
								appSource: connector.source,
								outboundAuthUrl: connector.outboundAuthUrl,
								analyticsAttributes: {
									source: TEMPLATE_ID,
									isNewlyEnabled: true,
								},
							})
							.onAuthorize(),
				})),
		[newlyEnabledConnectors],
	);

	// isEligible and connectorNudgeMessageProps depend only on apps, so they
	// only change when newlyEnabledConnectors changes — not on every render.

	const isEligible = useCallback(
		(message: PlacementMessage): boolean => {
			const raw = message['dismissedConnectorIds'];
			const dismissedConnectorIds = typeof raw === 'string' ? raw : '';
			const dismissed = dismissedConnectorIds ? dismissedConnectorIds.split(',') : [];
			return apps.some((app) => !dismissed.includes(app.connectorId));
		},
		[apps],
	);

	const connectorNudgeMessageProps = useMemo<ConnectorNudgeMessageProps>(() => ({ apps }), [apps]);

	return { templateId: TEMPLATE_ID, isEligible, connectorNudgeMessageProps };
};
